<script setup>
import { computed, ref } from 'vue'
import { useRouter } from 'vue-router'
import { useEquipmentsStore } from '@/store/equipments.js'
import { resolveImageUrl } from '@/composables/useImageUrl'

const store = useEquipmentsStore()
const router = useRouter()
const imgErrors = ref(new Set())

const topCategories = computed(() =>
  store.categories.filter(c => !c.parent).slice(0, 5)
)

const getAdCount = (category) => Number(category?.ad_count) || 0

const goToCategory = async (slug) => {
  await router.push({ name: 'category-details', query: { cat: slug } })
  window.location.reload()
}

const goToBrowse = () => {
  router.push({ name: 'categories' })
}

const handleImageError = (e, cat) => {
  imgErrors.value.add(cat.id)
}
</script>

<template>
  <section v-if="topCategories.length" class="py-8">
    <!-- Header -->
    <div class="flex items-center justify-between mb-4">
      <div>
        <h2 class="text-xl sm:text-2xl font-bold text-theme-text">Popular Categories</h2>
        <p class="text-sm text-theme-muted mt-0.5">Browse by what you need</p>
      </div>
      <button
        @click="goToBrowse"
        class="inline-flex items-center gap-1.5 px-5 py-2 rounded-full bg-primary-600 hover:bg-primary-500 text-white text-xs font-semibold transition-all duration-200 shadow-sm hover:shadow"
      >
        See all <i class="pi pi-arrow-right text-[10px]"></i>
      </button>
    </div>

    <!-- Category cards -->
    <div class="grid grid-cols-2 sm:grid-cols-3 md:grid-cols-5 gap-3 sm:gap-4">
      <button
        v-for="category in topCategories"
        :key="category.id"
        @click="goToCategory(category.slug)"
        class="group text-left bg-theme-card rounded-xl overflow-hidden border border-theme-border
               hover:shadow-lg transition-all duration-300 hover:-translate-y-1
               focus:outline-none focus:ring-2 focus:ring-primary-500/40"
      >
        <!-- Image -->
        <div class="relative h-28 sm:h-32 bg-theme-bg2 overflow-hidden">
          <img
            v-if="category.image && !imgErrors.has(category.id)"
            :src="resolveImageUrl(category.image)"
            :alt="category.name"
            class="w-full h-full object-cover group-hover:scale-105 transition-transform duration-500"
            loading="lazy"
            @error="handleImageError($event, category)"
          />
          <div
            v-else
            class="w-full h-full flex items-center justify-center bg-gradient-to-br from-primary-500/10 to-secondary-500/10"
          >
            <i class="pi pi-box text-3xl text-theme-muted"></i>
          </div>
        </div>

        <!-- Info -->
        <div class="px-3 py-2.5">
          <h3 class="font-semibold text-theme-text text-sm group-hover:text-primary-600 transition-colors truncate">
            {{ category.name }}
          </h3>
          <p class="text-xs text-theme-muted mt-0.5">
            {{ getAdCount(category) }} listings
          </p>
        </div>
      </button>
    </div>
  </section>
</template>
